// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import { ref, createRef } from 'lit/directives/ref.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { _x } from '@wordpress/i18n';

import {
	getConditionPostData,
	conditionPostDataHasFields,
	getAndSaveConditionFields
} from '@automator/builder/_legacy/js/item/conditions/utilities';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing a form for a specific condition's fields.
 * It displays a loading placeholder while fetching field data asynchronously.
 *
 * @augments LitElement
 */
@customElement( 'uap-builder-legacy-condition-form' )
export class AutomatorBuilderLegacyConditionForm extends LitElement {
    // Define style
    static styles = css`${ unsafeCSS( styles ) }`;

    /**
     * The condition to display.
     * @type {Object}
     */
    @property( { type: Object, attribute: 'condition' } )
    accessor condition;

    /**
     * The parent ID of the condition.
     * @type {String}
     */
    @property( { type: String, attribute: 'parent-id' } )
    accessor parentID;

    /**
     * The integration code of the condition.
     * @type {String}
     */
    @property( { type: String, attribute: 'integration-code' } )
    accessor integrationCode;

    /**
     * The condition code of the condition.
     * @type {String}
     */
    @property( { type: String, attribute: 'condition-code' } )
    accessor conditionCode;

    /**
     * The fields values to display.
     * @type {Object}
     */
    @property( { type: Object, attribute: false } )
    accessor fields;

    /**
     * The fields directory to display. This property is populated
     * asynchronously after the component is rendered.
     * @type {Object}
     */
    @property( { type: Object, attribute: false } )
    accessor fieldsDirectory;

    /**
     * The form reference.
     * @type {Ref}
     */
    $form = createRef();

    /**
	 * Disable the shadow DOM
	 *
	 * @todo Remove this method once fields become web components. This component should be using the shadow DOM.
	 * @return {Element} The element
	 */
	createRenderRoot() {
		return this;
	}

    /**
     * Renders the component.
     *
     * It first checks if the fields have already been loaded. If so, it renders
     * them immediately. Otherwise, it uses the `until` directive to show a
     * placeholder while asynchronously fetching and rendering the fields.
     *
     * @return {TemplateResult} The template to render.
     */
    render() {
        // If fields are already loaded, render them directly.
        if ( this.fieldsDirectory ) {
            return this._renderFields();
        }

        // Use the `until` directive to show a placeholder while we fetch the fields.
        // The `_loadAndRenderFields` method returns a promise that resolves with the
        // template for the fields.
        return html`
            ${ until(
                this._loadAndRenderFields(),
                this._templateFieldsPlaceholder
            ) }
        `;
    }

    /**
     * Asynchronously loads the fields data and returns a template to render it.
     * This is the correct pattern for using the `until` directive, where the
     * async operation returns the content to be rendered upon completion.
     *
     * @private
     * @return {Promise<TemplateResult>} A promise that resolves to the rendered fields template.
     */
    async _loadAndRenderFields() {
        // Check if the condition's fields need to be fetched from the server.
        if ( ! conditionPostDataHasFields( {
            integrationCode: this.integrationCode,
            conditionCode: this.conditionCode
        } ) ) {
            // Fetch and store the fields in a global state or cache.
            await getAndSaveConditionFields( {
                integrationCode: this.integrationCode,
                conditionCode: this.conditionCode
            } );
        }

        // Retrieve the fields data from the global state.
        const fields = this.conditionPostData.fields;

        // Update the component's property. This caches the result on the component
        // instance, preventing re-fetching if the component re-renders.
        this.fieldsDirectory = fields;

        // Check if fields are empty and return a "no options" message.
        if ( isEmpty( this.fieldsDirectory ) ) {
            return html`
                <div class="uap-conditions-options-fields-empty">
                    ${_x( 'This condition has no options.', 'Condition form', 'uncanny-automator' )}
                </div>
            `;
        }

        // Return the template that renders the fields.
        return this._renderFields();
    }

    /**
     * Returns a template for rendering the loaded fields.
     *
     * @private
     * @return {TemplateResult} The TemplateResult for the fields.
     */
    _renderFields() {
        return html`
            <div class="uap-conditions-options-fields">
                <uap-builder-item-legacy-form
					parent-id=${ this.parentID }
					item-id=${ this.condition.id }
					item-type="condition"

					.fieldsDirectory=${
						// Merge all fields in a single group. We don't need groups here yet, because the sentence is not rendering sentence pills
						Object.values( this.fieldsDirectory )
							.reduce( ( acc, val ) => acc.concat( val ), [] )
					}
					
					.fieldsValues=${ this.fields }

					return-format="legacy-meta"

					${ ref( this.$form ) }

					@uap-builder-legacy-form-change=${ ( event ) => {
						this.unsavedFieldValues = event.detail.unsavedFieldValues;
					} }
				></uap-builder-item-legacy-form>
            </div>
        `;
    }

    /**
     * Returns a placeholder to show while the fields are loading.
     *
     * @return {TemplateResult} The TemplateResult of the placeholder.
     */
    get _templateFieldsPlaceholder() {
        return html`
            <div class="uap-conditions-options-fields-placeholder">
                <div>
                    <uo-placeholder width="100px"></uo-placeholder> <uo-placeholder width="30px"></uo-placeholder>
                </div>
                <div>
                    <uo-placeholder height="42px"></uo-placeholder>
                </div>
                <div>
                    <uo-placeholder width="40px"></uo-placeholder> <uo-placeholder width="100px"></uo-placeholder> <uo-placeholder width="50px"></uo-placeholder>
                </div>
            </div>
        `;
    }

    /**
     * Returns the condition post data from a global state or cache.
     *
     * @return {Object} Object with useful data about the condition (like sentence and fields).
     */
    get conditionPostData() {
        return getConditionPostData( {
            integrationCode: this.integrationCode,
            conditionCode: this.conditionCode,
        } );
    }

    /**
     * Returns true if the fields are valid.
     *
     * @return {Boolean} True if the fields are valid.
     */
    get areFieldsValid() {
        return this.$form.value.areFieldsValid;
    }
}
